import { useCallback, useRef, useState } from 'react';
import GoogleMapReact from 'google-maps-react-markers';
import flattenChildren from 'react-keyed-flatten-children';
import { useDispatch } from '@client/hooks/redux.hooks';
import { setMap, setZoom } from '@client/slices/features/map.reducer';
import { useGoogleMaps } from '@client/components/forms/GoogleSearch/useGoogleMaps.hook';
import { MapZoomContext } from './MapContext';
import mapStyle from './mapStyle.json';
import { zIndex } from './common/constants';
import type { ChangeEventProps, LoadedProps } from './interfaces/GoogleMapReact';
import type { MapProps } from './interfaces';

export const Map: React.FC<MapProps> = ({
  location,
  zoom,
  children,
  height,
  MapOptions = {},
  onChange = () => {},
  onGoogleApiLoaded = () => {},
  className = '',
  loadGoogleScriptExternally = false,
  ...restProps
}) => {
  const dispatch = useDispatch();

  const [currentZoom, setCurrentZoom] = useState(zoom);
  const mapInstance = useRef(null);

  const handleMapLoaded = useCallback((props: LoadedProps) => {
    mapInstance.current = props.map;
    dispatch(setMap(props.map));

    onGoogleApiLoaded(props);
  }, [dispatch, onGoogleApiLoaded]);

  const handleMapChange = useCallback((props: ChangeEventProps) => {
    setCurrentZoom(props.zoom);
    dispatch(setZoom(props.zoom));

    onChange(props, mapInstance.current);
  }, [dispatch, onChange]);

  const loaded = useGoogleMaps(loadGoogleScriptExternally);

  return (
    <div
      style={{ height, width: '100%', zIndex: zIndex.map }}
      className={className}
    >
      <MapZoomContext.Provider value={currentZoom}>
        <GoogleMapReact
          apiKey={process.env.GOOGLE_MAPS_API_KEY}
          defaultCenter={location}
          defaultZoom={zoom}
          onChange={handleMapChange}
          onGoogleApiLoaded={handleMapLoaded}
          status={loaded ? 'ready' : 'loading'}
          loadScriptExternally
          options={{
            styles: mapStyle as any,
            controlSize: 37,
            disableDefaultUI: true,
            mapTypeControl: false,
            scaleControl: false,
            streetViewControl: false,
            rotateControl: false,
            fullscreenControl: false,
            keyboardShortcuts: false,
            zoomControl: true,
            zoomControlOptions: {
              position: 3, // TOP_RIGHT
            },
            ...MapOptions || {},
          }}
          {...restProps}
        >
          {flattenChildren(children)}
        </GoogleMapReact>
      </MapZoomContext.Provider>
    </div>
  );
};

export default Map;
